import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Container, Grid, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { ArrowUpRight, MessageCircle, Play, Star, Users } from 'lucide-react';

const heroContent = {
  heading: 'We Build Websites That Grow Your Business',
  subtext: 'Real client projects. Real results. Trusted across industries.',
  accent: 'Grow',
};

const heroShowcaseWebsites = [
  { title: 'Bihar Couture Runway', url: 'https://www.biharcouturerunway.com/' },
  { title: 'Primus College', url: 'https://www.primuscollege.co.in/' },
  { title: 'Planet Patna', url: 'https://www.planetpatna.com/' },
  { title: 'Mridani Art', url: 'https://mridaniart.com/' },
  { title: 'Nihans Foundation', url: 'https://www.nihanshfoundation.in/' },
];

const caseStudies = [
  {
    name: 'Bihar Couture Runway',
    category: 'Fashion & Events Platform',
    url: 'https://www.biharcouturerunway.com/',
    tagline: 'Bringing the Runway Experience Online',
    about: 'A fashion runway event needed a bold digital presence to showcase designers, models, and the overall brand experience to a wider audience.',
    whatWeDid: 'We designed a visually striking, fashion-forward website with event highlights, gallery sections, and a strong, memorable brand identity.',
    result: 'A high-impact online presence that elevates brand visibility and engages the fashion community.',
    technologies: 'React.js, MySQL',
    contact: 'hello@revivingindia.in',
  },
  {
    name: 'Primus College',
    category: 'College Website',
    url: 'https://www.primuscollege.co.in/',
    tagline: 'Empowering Education with a Strong Digital Identity',
    about: 'The college required a professional platform to present courses, facilities, and admissions information to prospective students and parents.',
    whatWeDid: 'We built a structured, informative website with clear course details, an admissions flow, and credibility-building content.',
    result: 'A trustworthy online identity that improves student engagement and supports admissions.',
    technologies: 'React.js, MySQL',
    contact: 'hello@revivingindia.in',
  },
  {
    name: 'Planet Patna',
    category: 'Museum Website',
    url: 'https://www.planetpatna.com/',
    image: '/planet-patna-showcase.jpg',
    tagline: 'Digitizing Culture with a Modern Experience',
    about: 'Planet Patna needed a digital platform to represent the cultural richness of the museum while making information easily accessible to visitors.',
    whatWeDid: 'We designed an immersive and visually engaging website with structured navigation, event highlights, and storytelling elements.',
    result: 'A modern digital presence that enhances visitor engagement and promotes cultural awareness online.',
    technologies: 'React.js, MySQL',
    contact: 'hello@revivingindia.in',
  },
  {
    name: 'Nihans Foundation',
    category: 'Rehabilitation Centre',
    url: 'https://www.nihanshfoundation.in/',
    tagline: 'Building Trust Through Digital Simplicity',
    about: 'The organization required a platform that could communicate services clearly while building trust among patients and families.',
    whatWeDid: 'We developed a clean and calming interface with clear service sections, easy contact options, and user-friendly navigation.',
    result: 'Improved accessibility and stronger trust for users seeking rehabilitation support.',
    technologies: 'React.js, MySQL',
    contact: 'hello@revivingindia.in',
  },
  {
    name: 'Zidan Graphic',
    category: 'Printing Company',
    url: 'https://www.zidangraphic.com/',
    tagline: 'Showcasing Services with Strong Brand Identity',
    about: 'A printing company needed a professional website to present its services and attract business clients.',
    whatWeDid: 'We built a corporate-style website highlighting services, portfolio, and business strengths with strong visual branding.',
    result: 'A credible online presence that supports client acquisition and brand visibility.',
    technologies: 'React.js, MySQL',
    contact: 'hello@revivingindia.in',
  },
  {
    name: 'Maa Dulari Pharmacy',
    category: 'Healthcare Website',
    url: 'https://maa-dulari-pharmacy.vercel.app/',
    tagline: 'Simplifying Healthcare Access Online',
    about: 'The goal was to create a simple and effective platform to showcase pharmacy services and products.',
    whatWeDid: 'We designed a clean, responsive interface focused on usability and easy navigation.',
    result: 'A user-friendly healthcare website improving customer reach and accessibility.',
    technologies: 'React.js, MySQL',
    contact: 'hello@revivingindia.in',
  },
  {
    name: 'Aviation Excellence',
    category: 'Training Institute',
    url: 'https://www.aviationexcellence.in/',
    tagline: 'Elevating Training Institutes with Digital Presence',
    about: 'The institute required a professional platform to present courses and attract aspiring aviation students.',
    whatWeDid: 'We developed a structured and informative website with course details, admission flow, and strong branding.',
    result: 'A powerful online identity that enhances credibility and student engagement.',
    technologies: 'React.js, MySQL',
    contact: 'hello@revivingindia.in',
  },
  {
    name: 'Mridani Art',
    category: 'E-commerce Clothing',
    url: 'https://mridaniart.com/',
    tagline: 'Crafting a Seamless Online Shopping Experience',
    about: 'A fashion brand needed a visually appealing and functional e-commerce platform to sell products online.',
    whatWeDid: 'We built a modern shopping experience with product categories, responsive design, and smooth navigation.',
    result: 'An engaging platform that supports online sales and brand growth.',
    technologies: 'React.js, MySQL',
    contact: 'hello@revivingindia.in',
  },
  {
    name: 'Agent India',
    category: 'AI Platform',
    url: 'https://www.agentindia.in/',
    tagline: 'Powering Businesses with AI-Driven Solutions',
    about: 'The platform required a modern interface to showcase AI capabilities and innovative solutions.',
    whatWeDid: 'We created a sleek, tech-focused design highlighting automation, AI tools, and advanced features.',
    result: 'A futuristic platform positioning the brand as a leader in AI solutions.',
    technologies: 'React.js, MySQL',
    contact: 'hello@revivingindia.in',
  },
  {
    name: 'DulariCare',
    category: 'E-commerce Platform',
    url: 'https://www.dularicare.com/',
    tagline: 'Building Scalable Digital Commerce',
    about: 'The client needed a robust e-commerce solution for managing products and online sales.',
    whatWeDid: 'We developed a scalable platform with product listings, cart system, and optimized checkout flow.',
    result: 'A complete online store ready for business growth and expansion.',
    technologies: 'React.js, MySQL',
    contact: 'hello@revivingindia.in',
  },
  {
    name: 'DulariCare Static',
    category: 'Static Deployment Version',
    url: 'http://dularicare.com.s3-website.ap-south-1.amazonaws.com/',
    tagline: 'Optimized for Performance & Scalable Hosting',
    about: 'A static version of the e-commerce platform to demonstrate lightweight deployment and fast loading.',
    whatWeDid: 'We deployed the application on scalable cloud infrastructure ensuring performance and reliability.',
    result: 'Improved speed, performance, and hosting efficiency.',
    technologies: 'React.js, MySQL',
    contact: 'hello@revivingindia.in',
  },
  {
    name: 'Mangalam Nasha Mukti Kendra',
    category: 'Rehabilitation Centre',
    url: 'https://www.mangalamnashamuktikendra.com/',
    tagline: 'Supporting Recovery with Clarity and Care',
    about: 'The center needed a trustworthy digital presence to communicate treatment services and help families find immediate support.',
    whatWeDid: 'We created a calm, structured website with clear treatment information, focused service pages, and direct inquiry pathways.',
    result: 'A compassionate online platform that improves trust, visibility, and patient-family engagement.',
    technologies: 'React.js, MySQL',
    contact: 'hello@revivingindia.in',
  },
  {
    name: 'Vaishnavi Nasha Mukti Kendra',
    category: 'Rehabilitation Centre',
    url: 'https://vaishanavinashamuktikendra.vercel.app/',
    tagline: 'Making Rehabilitation Access Simple and Reliable',
    about: 'The objective was to present rehabilitation services in a clear format so families and patients can quickly understand available support.',
    whatWeDid: 'We built a responsive service-led website with intuitive navigation, confidence-building content, and strong call-to-action flow.',
    result: 'A user-focused digital presence that strengthens credibility and increases consultation inquiries.',
    technologies: 'React.js, MySQL',
    contact: 'hello@revivingindia.in',
  },
];

const itemVariants = {
  hidden: { y: 40, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.6, ease: 'easeOut' as const },
  },
};

const previewOf = (url: string) => `https://s.wordpress.com/mshots/v1/${encodeURIComponent(url)}?w=1200`;
const HEADING_FONT = '"Sora", "Outfit", "Poppins", "Inter", sans-serif';
const BODY_FONT = '"Manrope", "DM Sans", "Inter", sans-serif';

const getDomainInsight = (category: string) => {
  if (category.includes('Museum')) {
    return 'For cultural and heritage platforms, clarity and storytelling are critical. We prioritize guided navigation, event visibility, and experience-driven layouts that keep visitors engaged.';
  }

  if (category.includes('Rehabilitation') || category.includes('Healthcare')) {
    return 'For healthcare-focused websites, trust and accessibility matter most. We use calm visual systems, clear service pathways, and frictionless contact points for families and patients.';
  }

  if (category.includes('Printing') || category.includes('Corporate')) {
    return 'For service and corporate businesses, we structure websites to communicate credibility fast: strong service hierarchy, proof sections, and conversion-oriented inquiry journeys.';
  }

  if (category.includes('Training')) {
    return 'For institutes and education brands, we design around admissions intent: course discovery, eligibility clarity, and structured CTAs that convert visitors into applicants.';
  }

  if (category.includes('E-commerce')) {
    return 'For e-commerce, we focus on product discovery, trust-building UI, and checkout flow optimization so the design supports real conversion, not just visual appeal.';
  }

  if (category.includes('AI')) {
    return 'For AI and technology products, we highlight capability, architecture, and business outcomes through modern visual language and high-information clarity.';
  }

  return 'For every domain, our approach starts with business goals, user behavior, and a conversion-focused content structure tailored to the audience.';
};

const OurCreationsPage: React.FC = () => {
  const [showcaseIndex, setShowcaseIndex] = useState(0);

  useEffect(() => {
    const showcaseTimer = setInterval(() => {
      setShowcaseIndex((prev) => (prev + 1) % heroShowcaseWebsites.length);
    }, 3500);

    return () => clearInterval(showcaseTimer);
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', pt: 10, pb: 10, overflowX: 'hidden', background: 'linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 55%, #F8FAFC 100%)' }}>
      <Container maxWidth={false} sx={{ px: { xs: 1.6, sm: 2.5, md: 4.5, lg: 6 } }}>
        <Box
          sx={{
            minHeight: { xs: 'auto', md: '68vh' },
            position: 'relative',
            overflow: 'visible',
            display: 'flex',
            alignItems: 'center',
            borderRadius: 0,
            px: { xs: 1.5, sm: 2.5, md: 4 },
            py: { xs: 3.25, sm: 4, md: 4.5 },
            background: 'linear-gradient(140deg, #0B1220 0%, #0F172A 45%, #111827 100%)',
            border: 'none',
            boxShadow: 'none',
            mb: 8,
            width: '100vw',
            maxWidth: '100vw',
            ml: 'calc(50% - 50vw)',
            mr: 'calc(50% - 50vw)',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ width: '100%' }}
          >
              <Grid container spacing={{ xs: 3, md: 5 }} alignItems="center">
                <Grid size={{ xs: 12, lg: 7 }}>
                  <motion.div variants={itemVariants} initial="hidden" animate="visible">
                    <Chip label="Our Creations" sx={{ mb: 2, bgcolor: 'rgba(37,99,235,0.2)', color: '#BFDBFE', border: '1px solid rgba(147,197,253,0.4)', fontWeight: 600, mx: { xs: 'auto', md: 0 }, display: 'inline-flex' }} />
                    <Typography sx={{ fontFamily: HEADING_FONT, fontSize: { xs: '1.65rem', sm: '2rem', md: '3.2rem' }, color: '#F1F5F9', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: { xs: 1.2, md: 1.15 }, mb: 2, textAlign: { xs: 'center', md: 'left' } }}>
                      {heroContent.heading.replace(heroContent.accent, '')}
                      <Box component="span" sx={{ background: 'linear-gradient(135deg, #60A5FA, #93C5FD)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        {heroContent.accent}
                      </Box>
                    </Typography>
                    <Typography sx={{ fontFamily: BODY_FONT, color: '#CBD5E1', fontSize: { xs: '0.95rem', md: '1.2rem' }, lineHeight: 1.6, mb: { xs: 2.5, md: 3.5 }, maxWidth: 700, textAlign: { xs: 'center', md: 'left' } }}>
                      {heroContent.subtext}
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4} sx={{ mb: 3.2 }}>
                      <Button component="a" href="/contact" variant="outlined" startIcon={<Play size={18} />} fullWidth={true} sx={{ borderColor: '#93C5FD', borderWidth: 2, color: '#93C5FD', px: 3.5, py: 1.2, borderRadius: '30px', textTransform: 'none', fontWeight: 600, maxWidth: { xs: '100%', sm: 'fit-content' }, '&:hover': { borderColor: '#BFDBFE', backgroundColor: 'rgba(147, 197, 253, 0.12)' } }}>
                        Talk to Us
                      </Button>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.4, sm: 3 }} alignItems={{ xs: 'center', sm: 'flex-start' }}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'center', sm: 'flex-start' }}>
                        <Star size={18} color="#FBBF24" fill="#FBBF24" />
                        <Typography sx={{ color: '#94A3B8', fontWeight: 500 }}>Real Client Projects</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'center', sm: 'flex-start' }}>
                        <Users size={18} color="#60A5FA" />
                        <Typography sx={{ color: '#94A3B8', fontWeight: 500 }}>Built Across Industries</Typography>
                      </Stack>
                    </Stack>
                  </motion.div>
                </Grid>

                <Grid size={{ xs: 12, lg: 5 }}>
                  <motion.div variants={itemVariants} initial="hidden" animate="visible" style={{ width: '100%' }}>
                    <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}>
                      <Box sx={{ position: 'relative', px: { xs: 0, md: 2 }, maxWidth: { xs: 300, sm: 360, md: 'none' }, mx: 'auto' }}>
                        <Box
                          sx={{
                            borderRadius: '20px 20px 14px 14px',
                            border: '1px solid rgba(148,163,184,0.35)',
                            boxShadow: '0 18px 42px rgba(2,6,23,0.45)',
                            background: 'linear-gradient(180deg, #111827 0%, #0F172A 100%)',
                            p: 1,
                          }}
                        >
                          <Box
                            sx={{
                              borderRadius: '14px',
                              overflow: 'hidden',
                              border: '1px solid rgba(148,163,184,0.25)',
                              background: '#020617',
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="flex-start"
                              sx={{ px: 1.5, py: 1, bgcolor: '#0B1220', borderBottom: '1px solid rgba(148,163,184,0.2)' }}
                            >
                              <Stack direction="row" spacing={0.8}>
                                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#EF4444' }} />
                                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#F59E0B' }} />
                                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#10B981' }} />
                              </Stack>
                            </Stack>

                            <Box
                              component="img"
                              src={previewOf(heroShowcaseWebsites[showcaseIndex].url)}
                              alt={heroShowcaseWebsites[showcaseIndex].title}
                              sx={{ width: '100%', height: { xs: 165, sm: 200, md: 270 }, objectFit: 'cover', display: 'block' }}
                            />
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            width: { xs: 95, md: 150 },
                            height: { xs: 8, md: 10 },
                            borderRadius: 10,
                            background: 'linear-gradient(180deg, #9CA3AF 0%, #6B7280 100%)',
                            mx: 'auto',
                            mt: 1.2,
                          }}
                        />
                        <Box
                          sx={{
                            width: { xs: 150, md: 230 },
                            height: { xs: 6, md: 8 },
                            borderRadius: 12,
                            background: 'linear-gradient(180deg, #6B7280 0%, #4B5563 100%)',
                            mx: 'auto',
                            mt: 0.8,
                          }}
                        />

                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-start"
                          sx={{
                            mt: 2,
                            flexWrap: 'nowrap',
                            overflowX: 'auto',
                            width: '100%',
                            px: 0.5,
                            pb: 0.4,
                            '&::-webkit-scrollbar': {
                              height: 4,
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'rgba(148,163,184,0.5)',
                              borderRadius: 6,
                            },
                          }}
                        >
                          {heroShowcaseWebsites.map((site, index) => (
                            <Chip
                              key={site.title}
                              label={site.title}
                              onClick={() => setShowcaseIndex(index)}
                              sx={{
                                bgcolor: showcaseIndex === index ? '#1D4ED8' : 'rgba(15,23,42,0.65)',
                                color: '#E2E8F0',
                                border: '1px solid rgba(148,163,184,0.35)',
                                cursor: 'pointer',
                                fontSize: { xs: '0.72rem', sm: '0.8125rem' },
                                height: { xs: 28, sm: 32 },
                                flex: '0 0 auto',
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </motion.div>
                  </motion.div>
                </Grid>
              </Grid>
          </motion.div>
        </Box>

        <Box sx={{ mt: { xs: 2, md: 4 } }}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
          >
            <Stack spacing={1.2} sx={{ mb: 4.5 }}>
              <Typography sx={{ fontFamily: HEADING_FONT, fontSize: { xs: '1.65rem', md: '2.4rem' }, fontWeight: 800, letterSpacing: '-0.02em', color: '#0F172A', lineHeight: 1.2 }}>
                Real Projects. Real Impact.
              </Typography>
              <Typography sx={{ fontFamily: BODY_FONT, color: '#475569', maxWidth: 760, lineHeight: 1.65 }}>
                Each website is crafted around business goals, user behavior, and conversion-focused structure. Explore the live case studies below.
              </Typography>
            </Stack>
          </motion.div>

          <Stack spacing={1.6}>
            {caseStudies.map((project, index) => {
              const isReverse = index % 2 === 1;

              return (
                <motion.div
                  key={project.name + project.url}
                  initial={{ opacity: 0, x: isReverse ? 30 : -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                >
                  <Box
                    sx={{
                      p: { xs: 1.2, md: 1.6 },
                      borderRadius: '24px',
                      background: 'linear-gradient(145deg, rgba(37,99,235,0.08) 0%, rgba(255,255,255,0.9) 46%, rgba(14,165,233,0.09) 100%)',
                      border: '1px solid #D6E6FF',
                      boxShadow: '0 16px 34px rgba(15,23,42,0.08)',
                    }}
                  >
                    <Grid
                      container
                      spacing={{ xs: 1, md: 1.4 }}
                      direction={{ xs: 'column', md: isReverse ? 'row-reverse' : 'row' }}
                      alignItems="stretch"
                    >
                      <Grid size={{ xs: 12, md: 5 }}>
                        <Box sx={{ height: '100%', position: 'relative', borderRadius: '14px', overflow: 'hidden', border: '1px solid #DBEAFE' }}>
                          <Box
                            component="img"
                            src={project.image || previewOf(project.url)}
                            alt={project.name}
                            sx={{ width: '100%', height: { xs: 170, md: '100%' }, minHeight: { md: 240 }, objectFit: 'cover', objectPosition: 'top', display: 'block' }}
                            onError={(e) => {
                              const target = e.currentTarget;
                              target.onerror = null;
                              target.src = previewOf(project.url);
                            }}
                          />
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, md: 7 }}>
                        <Box
                          sx={{
                            height: '100%',
                            p: { xs: 1.35, md: 1.6 },
                            borderRadius: '14px',
                            background: '#FFFFFF',
                            border: '1px solid #E2E8F0',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.8,
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.2}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontFamily: HEADING_FONT, fontWeight: 800, color: '#0F172A', fontSize: { xs: '0.98rem', md: '1.08rem' } }}>
                                {project.name}
                              </Typography>
                              <Typography sx={{ fontFamily: BODY_FONT, color: '#64748B', fontSize: '0.8rem' }}>{project.category}</Typography>
                            </Box>
                            <Chip label="Live" sx={{ bgcolor: '#DBEAFE', color: '#1D4ED8', border: '1px solid #BFDBFE', fontWeight: 700, flexShrink: 0 }} />
                          </Stack>

                          <Typography sx={{ fontFamily: HEADING_FONT, color: '#1E40AF', fontWeight: 700, fontSize: { xs: '0.85rem', md: '0.92rem' } }}>
                            {project.tagline}
                          </Typography>

                          <Stack spacing={0.95} sx={{ color: '#334155', fontSize: '0.88rem' }}>
                            <Typography sx={{ fontFamily: BODY_FONT, color: '#334155', lineHeight: 1.65 }}>
                              {project.about}
                            </Typography>
                            <Typography sx={{ fontFamily: BODY_FONT, color: '#334155', lineHeight: 1.65 }}>
                              {project.whatWeDid}
                            </Typography>
                            <Typography sx={{ fontFamily: BODY_FONT, color: '#334155', lineHeight: 1.65 }}>
                              {project.result}
                            </Typography>
                            <Typography sx={{ fontFamily: BODY_FONT, color: '#1E3A8A', lineHeight: 1.65 }}>
                              {getDomainInsight(project.category)}
                            </Typography>
                            <Typography sx={{ fontFamily: BODY_FONT, color: '#0F172A', lineHeight: 1.6, fontWeight: 700 }}>
                              If you want a website like this for your business, contact us and we will plan your custom solution.
                            </Typography>
                          </Stack>

                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 0.6 }}>
                            <Button
                              component="a"
                              href={project.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="contained"
                              endIcon={<ArrowUpRight size={16} />}
                              sx={{
                                alignSelf: 'flex-start',
                                background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                                borderRadius: '999px',
                                textTransform: 'none',
                                fontWeight: 700,
                                px: 2.2,
                                boxShadow: '0 10px 20px rgba(37,99,235,0.26)',
                                '&:hover': { background: 'linear-gradient(135deg, #1D4ED8, #1E40AF)' },
                              }}
                            >
                              Visit Website
                            </Button>
                            <Button
                              component="a"
                              href="/contact"
                              variant="outlined"
                              sx={{
                                alignSelf: 'flex-start',
                                borderColor: '#93C5FD',
                                color: '#1D4ED8',
                                borderRadius: '999px',
                                textTransform: 'none',
                                fontWeight: 700,
                                px: 2.2,
                                '&:hover': { borderColor: '#60A5FA', bgcolor: '#EFF6FF' },
                              }}
                            >
                              Contact Us
                            </Button>
                          </Stack>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </motion.div>
              );
            })}
          </Stack>
        </Box>

        <Box sx={{ mt: { xs: 4, md: 6 } }}>
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: '22px',
              border: '1px solid #CFE1FF',
              background: 'linear-gradient(135deg, #0F172A 0%, #1E3A8A 55%, #2563EB 100%)',
              boxShadow: '0 22px 42px rgba(15, 23, 42, 0.3)',
            }}
          >
            <Stack spacing={1.5}>
              <Typography sx={{ fontFamily: HEADING_FONT, color: '#FFFFFF', fontWeight: 800, letterSpacing: '-0.02em', fontSize: { xs: '1.4rem', md: '2rem' }, lineHeight: 1.2 }}>
                Ready to Build a Website That Brings Real Results?
              </Typography>
              <Typography sx={{ fontFamily: BODY_FONT, color: '#DBEAFE', lineHeight: 1.7, maxWidth: 920 }}>
                Your website should not just look good — it should grow your business, build trust, and convert visitors into clients. At Reviving India, we design high-performing digital experiences tailored to your domain, your audience, and your growth goals.
              </Typography>
              <Typography sx={{ fontFamily: BODY_FONT, color: '#E2E8F0', lineHeight: 1.7, maxWidth: 920 }}>
                Whether you run a startup, institute, e-commerce brand, healthcare center, or service business, we can plan, design, and launch your website with strategy-first execution.
              </Typography>

              <Button
                component="a"
                href="/contact"
                variant="contained"
                sx={{
                  alignSelf: 'flex-start',
                  mt: 0.5,
                  bgcolor: '#FFFFFF',
                  color: '#1D4ED8',
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: '999px',
                  px: 3,
                  py: 1,
                  '&:hover': { bgcolor: '#EFF6FF' },
                }}
              >
                Contact Us
              </Button>

              <Typography sx={{ fontFamily: BODY_FONT, color: '#BFDBFE', fontSize: { xs: '0.84rem', md: '0.9rem' }, lineHeight: 1.6 }}>
                Avg launch time: 4-7 days • Domain-first strategy • Mobile-first build • Conversion-focused structure
              </Typography>
            </Stack>
          </Box>
        </Box>

      </Container>

      <Box
        component="a"
        href="https://wa.me/919876543210?text=Hi%20Reviving%20India,%20I%20want%20a%20website%20for%20my%20business."
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        sx={{
          position: 'fixed',
          right: { xs: 14, md: 24 },
          bottom: { xs: 16, md: 22 },
          width: { xs: 54, md: 58 },
          height: { xs: 54, md: 58 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #25D366 0%, #1FA855 100%)',
          color: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          boxShadow: '0 14px 28px rgba(37, 211, 102, 0.35)',
          zIndex: 1300,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          fontFamily: BODY_FONT,
          fontWeight: 800,
          fontSize: { xs: '0.7rem', md: '0.74rem' },
          textDecoration: 'none',
          '&:hover': {
            transform: 'translateY(-2px) scale(1.04)',
            boxShadow: '0 18px 34px rgba(37, 211, 102, 0.45)',
          },
        }}
      >
        <MessageCircle size={21} color="#FFFFFF" strokeWidth={2.6} />
        <Box component="span" sx={{ color: '#FFFFFF', lineHeight: 1 }}>WA</Box>
      </Box>
    </Box>
  );
};

export default OurCreationsPage;
